import { format, 
    addDays, 
    addHours, 
    addMinutes,
    isValid, 
    parseISO, 
    parse, 
    getHours, 
    isEqual, 
    isBefore, 
    isAfter, 
    endOfMonth,
    differenceInMinutes, 
    differenceInDays, 
    differenceInCalendarDays,  
    eachDayOfInterval, 
    startOfDay,
} from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import chineseLunar from 'chinese-lunar';
import { TIMEZONE_OFFSET_MINUTES,
    REPORTING_MINUTES, 
    DEBRIEFING_MINUTES,
} from 'state/constants';


/* ---------- FLAG, COLOR ---------- */
export const getFlagUrl = (countryCode) => {
    const safeCode = countryCode.toLowerCase();
    return `https://flagcdn.com/w320/${safeCode}.png`;
};

export const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};


/* ---------- NUMBER ---------- */
export const formatNumber = (
    value, 
    minimumFractionDigits = 0, 
    maximumFractionDigits = 0,
) => {
    if (value === null || value === undefined || isNaN(value)) return "";
    return new Intl.NumberFormat("vi-VN", {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(value);
};

export const safeMin = (arr) => (arr.length ? Math.min(...arr) : 0);
export const safeMax = (arr) => (arr.length ? Math.max(...arr) : 0);
export const safeAvg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;


/* ---------- DATE ---------- */
export const formatDate = (d, formatString = 'dd/MM/yyyy', fallback = '-') => 
    d ? format(new Date(d), formatString) : fallback;

export const formatTZDate = (date, tz = "UTC", format = "dd/MM/yyyy", fallback = "") => {
    if (!date) return fallback;
    try {
        return formatInTimeZone(date, tz, format);
    } catch {
        return fallback;
    }
};

export const isDateInRange = (startDate, endDate, targetDate) => {
    return targetDate >= startDate && targetDate <= endDate;
};

export const isDayInHolidays = (holidays, date) => {
    const formattedDate = date.toISOString().slice(5, 10); // Format date as MM/DD
    return holidays.includes(formattedDate);
};

export const isInWOCL = (date) => {
    if (!date) return false;
    const hour = date.getHours();
    return hour >= 1 && hour < 6;
}

export const splitDateRange = (start, end, chunkDays = 90) => {
    const ranges = [];
    let cursor = new Date(start);

    while (cursor <= end) {
        const nextStart = addDays(cursor, chunkDays);
        let chunkEnd = addDays(cursor, chunkDays - 1);
        chunkEnd = chunkEnd < end ? chunkEnd : end;
        
        ranges.push({
            start: cursor,
            end: chunkEnd,
        });

        cursor = nextStart; 
    }

    return ranges;
};

export const sanitizeDates = (obj) => {
    if (obj instanceof Date) return obj.toISOString();
    if (Array.isArray(obj)) return obj.map(sanitizeDates);
    if (obj && typeof obj === "object") {
        return Object.fromEntries(
            Object.entries(obj).map(([k, v]) => [k, sanitizeDates(v)])
        );
    }
    return obj;
};

export const getDatesBetween = (start, end) =>
    eachDayOfInterval({
        start: startOfDay(start),
        end: startOfDay(end),
    });

export const getAdjustedEndDate = (endDate) => {
    const offset = 7;
    const today = new Date();
    const endDayOfMonth = endOfMonth(today);

    if (isBefore(endDate, endDayOfMonth) || isEqual(endDate, endDayOfMonth)) {
        return endDate;
    }

    if (isAfter(endDate, endDayOfMonth) && differenceInDays(endDate, today) > offset) {
        return endDayOfMonth;
    }

    return addDays(today, offset);
};

export const insertColonToTime = (timeString) => {
    return timeString.slice(0, 2) + ":" + timeString.slice(2);
};

export const timeStringToMinutes = (timeString) => {
    if (!timeString || timeString.trim() === "") return 0;
    
    const [hours, minutes] = timeString.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
};    
  
export const timeStringToHours = (timeString) => {
    if (!timeString || timeString.trim() === "") return 0;

    const [hours, minutes] = timeString.split(':').map(Number);
    return (hours || 0) + (minutes / 60 || 0);
};    

export const minutesToTimeString = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

export const normalizeDate = (d) => {
    if (!d) return null;
    const nd = new Date(d);
    // Nếu là chuỗi 'YYYY-MM-DD' thì parse theo local để tránh Safari hiểu sai
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day);
    }
    nd.setHours(0, 0, 0, 0);
    return nd;
};

export const safeDifferenceInCalendarDays = (endDate, startDate) => {
    if (!startDate || !endDate) return 0;

    const start = normalizeDate(startDate);
    const end = normalizeDate(endDate);

    return differenceInCalendarDays(end, start);
};

export const convertLunarRangeToSolarByYear = (lunarStart, lunarEnd, year) => {
  const startDay = format(lunarStart, "dd");
  const startMonth = format(lunarStart, "MM");

  const endDay = format(lunarEnd, "dd");
  const endMonth = format(lunarEnd, "MM");

  const solarStart = convertLunarToSolar(
    `${year}-${startMonth}-${startDay}`
  );

  let solarEnd = convertLunarToSolar(
    `${year}-${endMonth}-${endDay}`
  );

  if (!solarStart || !solarEnd) return null;

  // Trường hợp lunar range bắc qua năm (vd 29/12 -> 03/01)
  if (solarEnd < solarStart) {
    solarEnd = convertLunarToSolar(
      `${year + 1}-${endMonth}-${endDay}`
    );
  }

  return { solarStart, solarEnd };
};

export const convertLunarToSolar = (lunarDate) => {
  if (!lunarDate) return null;

  // Lunar Date is in "yyyy-MM-dd" format
  const [year, month, day] = lunarDate.split('-').map(Number);
  const solarDate = chineseLunar.lunarToSolar(year, month, day);
  
  return solarDate;
};

export const parseDate = (dateStr, formatStr = 'ddMMyyyy') => {
    let date = parse(dateStr, formatStr, new Date());
    date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return date = date.toISOString();
};

export const parseUTCDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())); // Ensures UTC day
};

export const parseDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) {
        return null;
    }

    const [day, month, year] = dateStr.split('/');  
    return new Date(`${year}-${month}-${day}T${timeStr}:00.000Z`);  
};

export const parseTimeToMinute = (timeStr) => {
    const regex = /^([0-9]{2}):([0-9]{2})$/; // Check time format
    const match = timeStr.match(regex);    
    if (!match) {
        throw new Error("Invalid time format. Expected HH:mm.");
    }
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);

    return hours * 60 + minutes;
}

export const parseTimeToDecimal = (timeStr) => {
    const parsedDate = parse(timeStr, 'HH:mm', new Date());
    const hours = parsedDate.getHours();
    const minutes = parsedDate.getMinutes();    
    return hours + minutes / 60;
}

export function addMinutesToTime(timeStr, minutesToAdd) {
    if (!timeStr) return null;

    const date = parse(timeStr, 'HH:mm', new Date());      
    const newDate = addMinutes(date, minutesToAdd);    
    return format(newDate, 'HH:mm');
}

export function addTimeToTime(timeStr, timeToAdd) {
    if (!timeStr || !timeToAdd) {
        return null;
    }

    const date = parse(timeStr, 'HH:mm', new Date());
    const [hoursToAdd, minutesToAdd] = timeToAdd.split(':').map(Number);
    const totalMinutesToAdd = hoursToAdd * 60 + minutesToAdd;
    const newDate = addMinutes(date, totalMinutesToAdd);
    return format(newDate, 'HH:mm');
}

export function addTimeToDate(dateTime, timeStr) {
    if (!dateTime || !timeStr) {
        return null;
    }

    let date = dateTime;
    if (!(dateTime instanceof Date)) {
        date = parseISO(dateTime);
    }
    const minutes = parseTimeToMinute(timeStr);
    const newDate = addMinutes(date, minutes);
    return newDate.toISOString();
}

export function addMinutesToDateTime(dateTime, minutesToAdd) {
    if (!dateTime) {
        return null;
    }
    
    let date = dateTime;
    if (!(dateTime instanceof Date)) {
        date = parseISO(dateTime);
    }
    
    const newDate = addMinutes(date, minutesToAdd);
    return newDate.toISOString();
}

export function getDateFromDateTime(dateTimeStr) {
    if (!dateTimeStr) return null;
    
    const date = new Date(dateTimeStr);
    
    return new Date(
        Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate()
        )
    ).toISOString();
}

export function getTimeFromDateTime(dateTimeStr) {
    if (!dateTimeStr) return null;

    const date = new Date(dateTimeStr);    
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
}

export const convertLocalTimeToUTC = (datetime) => {
    const year = datetime.getFullYear();
    const month = datetime.getMonth();
    const date = datetime.getDate();
    const hours = datetime.getHours();
    const minutes = datetime.getMinutes();
    const seconds = datetime.getSeconds();

    return new Date(Date.UTC(year, month, date, hours, minutes, seconds));
};

export function convertToLocalTime(dateTimeStr, timeZone) {  
    return toZonedTime(dateTimeStr, timeZone).toISOString();
}

export function combineDateAndTime(dateStr, timeStr) {
    if (!dateStr) {
        return null;
    }

    const baseDate = typeof dateStr === 'string' 
        ? parseISO(dateStr) 
        : new Date(dateStr);

    if (!isValid(baseDate)) {
        return null;
    }

    let hours = 0;
    let minutes = 0;
    if (typeof timeStr === 'string' && timeStr.includes(':')) {
        const timeArr = timeStr.split(':');

        hours = Number(timeArr[0]);
        minutes = Number(timeArr[1]);

        if (
            isNaN(hours) ||
            isNaN(minutes) ||
            hours < 0 || hours > 23 ||
            minutes < 0 || minutes > 59
        ) {
            return null;
        }
    }

    let combinedDate = baseDate;
    combinedDate = addHours(combinedDate, hours);
    combinedDate = addMinutes(combinedDate, minutes);

    return combinedDate.toISOString();
}

export const convertTimeToDecimal = (time) => {
    if (!time) {
        return 0;
    }
    const [hours, minutes] = time.split(':').map(Number);   
    const decimal = hours + (minutes / 60);    
    return decimal;
}

export const convertDecimalToTime = (decimal) => {
    if (decimal == null || isNaN(decimal)) {
        return "00:00";
    }

    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');

    return `${hh}:${mm}`;
};

export const formatRelativeTime = (isoString) => {
  if (!isoString) return "";
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)  return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)     return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
};

function isISODateString(value) {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z)?$/.test(value);
}

function isDateString(value) {
  if (typeof value !== "string") return false;

  // Chuỗi dạng yyyy-MM-dd
  const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/;

  // Chuỗi dạng ISO 8601: yyyy-MM-ddTHH:mm:ss(.sss)?Z hoặc +07:00
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

  return simpleDateRegex.test(value) || isoDateRegex.test(value);
}

const isDdMMMDate = (value) => {
    return /^\d{2}[A-Za-z]{3}$/.test(value); // Matches ddMMM format, e.g., 01Jan
};

const parseDdMMMDate = (dateStr) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = parseInt(dateStr.slice(0, 2), 10);
    const month = months.indexOf(dateStr.slice(2));
    if (month === -1) {
        throw new Error(`Invalid month abbreviation in date string: ${dateStr}`);
    }
    const currentYear = new Date().getFullYear();
    
    return new Date(currentYear, month, day); 
};


/* ---------- STRING ---------- */
export const capitalizeFirstLetter = (str) => {   
    return str.length === 0 ? str : str.charAt(0).toUpperCase() + str.slice(1);
}

export const titleCase = (str) => {
    return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
}

export const safeSplit = (str, limit) => {
    if (!str || typeof str !== 'string') return '';
    return str.split(',')?.slice(0, limit)?.map(s => s.trim()).filter(Boolean).join(", ");
}

export const splitString = (str, char, n) => {
    if (!str) return null;     
    const elements = str?.split(char);
    if (n >= 0 && n < elements.length) {
        return elements[n];
    } else {
        return null;
    }
}

export const splitToArray = (str, separators = /[;,|]/) => {
    if (!str) return [];

    return str
        ?.split(separators)
        ?.map(item => item.trim())
        ?.filter(Boolean);
};

export const pluralize = (word, n) => {
    if (n === 0 || Math.abs(n) === 1) return word;

    const IRREGULAR_PLURALS = {
        person: 'people',
        man: 'men',
        woman: 'women',
        child: 'children',

        mouse: 'mice',
        goose: 'geese',
        tooth: 'teeth',
        foot: 'feet',

        aircraft: 'aircraft',
        sheep: 'sheep',
        deer: 'deer',
        fish: 'fish',
        species: 'species',
        series: 'series',

        datum: 'data',
        medium: 'media',
        analysis: 'analyses',
        basis: 'bases',
        criterion: 'criteria',
        phenomenon: 'phenomena',
    };

    if (IRREGULAR_PLURALS[word]) return IRREGULAR_PLURALS[word];

    // consonant + y → ies
    if (/[bcdfghjklmnpqrstvwxyz]y$/i.test(word)) {
        return word.replace(/y$/i, 'ies');
    }

    // s, x, z, ch, sh, o → es
    if (/(s|x|z|ch|sh|o)$/i.test(word)) {
        return `${word}es`;
    }

    // default
    return `${word}s`;
};



/* ---------- SORT ---------- */
export function getSortableValue(x) {
    if (x instanceof Date) return x.getTime();
    if (typeof x === "string" && isISODateString(x)) return new Date(x).getTime(); 
    if (typeof x === "string" && isDdMMMDate(x)) return parseDdMMMDate(x).getTime(); 
    if (typeof x === "number" || "string") return x; 

    return Number.MAX_VALUE;
}

export function sortMultipleFields(arr, fields, isCheckNumber) {
    const convertNumber = (value) => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            if (/^\d+(\.\d+)?$/.test(value.trim())) { 
                return parseFloat(value);
            }
        }
        return null;
    };

    return [...arr].sort((a, b) => { 
        for (let { field, direction = 'asc' } of fields) {
            const dir = direction === 'asc' ? 1 : -1;

            let valA = a[field] ?? null;
            let valB = b[field] ?? null;

            if (valA === null && valB === null) continue;
            if (valA === null) return -1 * dir; 
            if (valB === null) return 1 * dir;

            if (isCheckNumber) {
                const numA = convertNumber(valA);
                const numB = convertNumber(valB);
    
                // Nếu cả hai đều là số → sort theo số
                if (numA !== null && numB !== null) {
                    if (numA > numB) return dir;
                    if (numA < numB) return -dir;
                    continue;
                }
    
                // Nếu một cái là số, một cái không → số trước
                if (numA !== null && numB === null) return -1 * dir;
                if (numA === null && numB !== null) return 1 * dir;
            }

            // Cả hai đều không phải số → sort chuỗi
            if (valA > valB) return dir;
            if (valA < valB) return -dir;
        }
        return 0;
    });
}


/* ---------- FORMAT ---------- */
export function formatXValue(x) {
    if (x instanceof Date) {
        return format(x, 'ddMMM'); 
    }

    if (typeof x === "string" && isDateString(x)) {
        return format(parseISO(x), 'ddMMM'); 
    }

    if (typeof x === "number") {
        return x.toString(); 
    }

    if (typeof x === "string") {
        // "yyyy.MM" → "MM.yyyy"  (month period key)
        if (/^\d{4}\.\d{2}$/.test(x)) {
            const [yyyy, mm] = x.split('.');
            return `${mm}.${yyyy}`;
        }
        // "yy.MM.dd - yy.MM.dd" → "dd.MM.yy - dd.MM.yy"  (week/year range key)
        if (/^\d{2}\.\d{2}\.\d{2} - \d{2}\.\d{2}\.\d{2}$/.test(x)) {
            return x.split(' - ')
                .map(p => { const [yy, mm, dd] = p.split('.'); return `${dd}.${mm}.${yy}`; })
                .join(' - ');
        }
        return String(x);
    }

    return x;
}


/* ---------- MUI GRID ---------- */
export const getSelectedIds = (rowSelectionModel, rows, idKey = '_id') => {
    if (!rowSelectionModel || !rows) return [];

    if (rowSelectionModel.type === 'include') {
        return Array.from(rowSelectionModel.ids);
    } else if (rowSelectionModel.type === 'exclude') {
        return rows
            .map(row => row[idKey])
            .filter(id => !rowSelectionModel.ids.has(id));
    }
    return [];
}

export const isAnyRowSelected = (rowSelectionModel, rows, idKey = '_id') => {
    return getSelectedIds(rowSelectionModel, rows, idKey).length > 0;
}


/* ---------- FLIGHT ---------- */
export function removeItemByValueImmutable(
    array = [],
    field,
    excludeStrings = []
) {
    if (!excludeStrings || excludeStrings.length === 0) return array;

    const keywords = excludeStrings.map(s => s.toLowerCase());

    return array.filter(item => {
        const value = item?.[field]?.toString().toLowerCase() || '';
        return !keywords.some(keyword => value.includes(keyword));
    });
}

export function filterItemByValueImmutable(
    array = [],
    field,
    includeStrings = []
) {
    if (!Array.isArray(includeStrings) || includeStrings.length === 0) {
        return array;
    }

    const keywords = includeStrings.map(s => s.toLowerCase());

    return array.filter(item => {
        const value = item?.[field]?.toString().toLowerCase() || '';
        return keywords.some(keyword => value.includes(keyword));
    });
}

export const parseDelays = (delayString) => {
    if (!delayString?.trim()) return 0;

    return delayString.split(';').reduce((acc, cur) => {
        const delays = cur.trim().split(' '); 
        const delayMinutes = delays.length === 1 ? Number(delays[0]) : Number(delays[1]);

        return acc + (isNaN(delayMinutes) ? 0 : delayMinutes);
    }, 0);
};

export const parseDelaysCode = (delayString) => {
    if (!delayString?.trim()) return '';

    return delayString.split(';')
        .map(part => {
            const delays = part.trim().split(' '); 
            return delays.length === 1 ? null : delays[0]; 
        })
        .filter(code => code !== null) 
        .join(' ');
};

export const parseCancelledReasons = (flightStatusStr) => {
    if (typeof flightStatusStr !== 'string') return null;

    const str = flightStatusStr.trim();
    if (!str) return null;

    const lower = str.toLowerCase();

    if (!lower.includes('cancelled') && !lower.includes('deleted')) {
        return null;
    }

    // Không có dấu ":"
    const idx = str.indexOf(':');
    if (idx === -1) return str;

    // Lấy phần sau dấu ":"
    const reason = str.slice(idx + 1).trim();

    return reason || 'Other';
};

export function countDistinctField(array, field) {
    const fieldValues = array.map(item => item[field]);
    const uniqueValues = new Set(fieldValues);
    return uniqueValues.size;
}

export function countDistinctFieldWithCondition(array, field, conditionField, excludeString) {    
    if (!Array.isArray(array) || !field || !conditionField) {
        return 0;
    }
    
    const fieldValues = array
        .filter(item => 
            item?.[conditionField] && 
            (!excludeString || !item[conditionField].toLowerCase().includes(excludeString.toLowerCase()))
        )
        .map(item => item?.[field])
        .filter(value => value !== null && value !== undefined);

    // Create a Set from the filtered field values to ensure uniqueness
    const uniqueValues = new Set(fieldValues);
    
    // Return the count of unique values
    return uniqueValues.size;
}

export const countArrayWithCondition = (array, field, conditionField, excludeString, isExclude) => {
    const result = {};

    array.forEach(item => {
        if (isExclude) {
            if (item[conditionField] && item[conditionField].toLowerCase().includes(excludeString.toLowerCase())) {
                return;
            }
        } else {
            if (item[conditionField] && !item[conditionField].toLowerCase().includes(excludeString.toLowerCase())) {
                return; 
            }
        }
        
        const fieldValue = item[field] || 'Unknown';

        if (!result[fieldValue]) {
            result[fieldValue] = 0;
        }

        result[fieldValue]++;
    });

    return result;
};

export const countArrayWithOutCondition = (array, field) => {
    const result = {};

    array.forEach(() => {       
        const fieldName = field || 'Unknown';  
        if (!result[fieldName]) {
            result[fieldName] = 0;
        }

        result[fieldName]++;
    });

    return result;
};

export const countBlockTime = (fullSta, fullStd) => {
    return fullStd && fullSta 
        ? (differenceInMinutes(fullSta, fullStd) > 0 ? differenceInMinutes(fullSta, fullStd) : null) 
        : null;
};

export const countTaxiOut = (takeOff, atd) => {
    return takeOff && atd ? differenceInMinutes(takeOff, atd) : null;
};

export const countTaxiIn = (ata, touchDown) => {
    return ata && touchDown ? differenceInMinutes(ata, touchDown) : null;
};

export const countAirTime = (touchDown, takeOff) => {
    return touchDown && takeOff ? differenceInMinutes(touchDown, takeOff) : null;
};

export const countTAT = (curEtd, preEta) => {
    return curEtd && preEta ? 
        (differenceInMinutes(curEtd, preEta) > 0 ? differenceInMinutes(curEtd, preEta) : null) 
        : null;
};

export const countBreakMinutes = (curRpt, preEod) => {
    return curRpt && preEod 
        ? (differenceInMinutes(curRpt, preEod) > 0 ? differenceInMinutes(curRpt, preEod) : null) 
        : null;
};

export const calcOTP = (total, delayed) =>
    total > 0 ? (1 - delayed / total) * 100 : 100;

export const calcHotelDays = (breakStart, breakEnd) => {
    if (!breakStart || !breakEnd) return 0;

    const breakMinutes = differenceInMinutes(breakEnd, breakStart);
    if (breakMinutes < 360) return 0; // <6h thì không cần hotel

    let hotelNights = 0;

    // --- Xử lý check-in ---
    const arrivalDay = new Date(breakStart);
    const standardCheckIn = new Date(arrivalDay.setHours(14, 0, 0, 0));
    const earlyMorning = new Date(arrivalDay.setHours(6, 0, 0, 0));

    if (breakStart < earlyMorning) {
        // Đến trước 06:00 => tính thêm 1 đêm
        hotelNights += 1;
    } else if (breakStart >= earlyMorning && breakStart < standardCheckIn) {
        // Đến trong khoảng 06:00 - 14:00 => nửa ngày
        hotelNights += 0.5;
    } else {
        // Đến sau 14:00 => theo chuẩn khách sạn
        hotelNights += 0;
    }

    // --- Xử lý check-out ---
    const departureDay = new Date(breakEnd);
    const standardCheckOut = new Date(departureDay.setHours(12, 0, 0, 0));
    const lateAfternoon = new Date(departureDay.setHours(18, 0, 0, 0));

    if (breakEnd > lateAfternoon) {
        // Rời sau 18:00 => tính thêm 1 đêm
        hotelNights += 1;
    } else if (breakEnd > standardCheckOut && breakEnd <= lateAfternoon) {
        // Rời trong khoảng 12:00 - 18:00 => nửa ngày
        hotelNights += 0.5;
    }

    // --- Tính số ngày nghỉ qua đêm ---
    const nightsBetween = differenceInCalendarDays(breakEnd, breakStart);
    hotelNights += nightsBetween > 0 ? nightsBetween : 0;

    return hotelNights;
}

const parsePickupParams = (str) => {
    const lookup = {};
    const sectorRe = /([A-Za-z0-9_ ]+)\s*:\s*\{([^}]*)\}/g;
    let m;
    while ((m = sectorRe.exec(str)) !== null) {
        const type = m[1].trim().toUpperCase();
        const values = {};
        m[2].split(",").forEach(pair => {
            const [k, v] = pair.split(":").map(s => s.trim());
            if (k && v) values[k.toUpperCase()] = Number(v);
        });
        lookup[type] = values;
    }
    return lookup;
};

export const calcPickupMinutes = (sectorType, dep, pickupParams) => {
    const lookup = typeof pickupParams === "string" 
        ? parsePickupParams(pickupParams) 
        : pickupParams;

    const values = lookup[sectorType?.toUpperCase()];
    if (!values) return 90;

    return values[dep?.toUpperCase()] ?? values["OTH"] ?? 90;
};

export const calcRPT = (flightDate, etd) => {    
    if (!flightDate) return null;
    const etdDate = parseISO(combineDateAndTime(flightDate, etd));
    const rpt = new Date(etdDate.getTime() - 1000 * 60 * REPORTING_MINUTES);
    return rpt.toISOString();
};

export const calcReportingTime = (fullEtd) => {    
    if (!fullEtd) return null;
    const etdDate = typeof fullEtd === 'string' ? parseISO(fullEtd) : new Date(fullEtd);    
    const rpt = new Date(etdDate.getTime() - 1000 * 60 * REPORTING_MINUTES);
    return rpt.toISOString();
};

export const calcEOD = (flightDate, eta) => {    
    if (!flightDate) return null;
    const etaDate = parseISO(combineDateAndTime(flightDate, eta));
    const eod = new Date(etaDate.getTime() + 1000 * 60 * DEBRIEFING_MINUTES);
    return eod.toISOString();
};

export const calcEndOfDuty = (fullEta) => {    
    if (!fullEta) return null;
    const etaDate = typeof fullEta === 'string' ? parseISO(fullEta) : new Date(fullEta);    
    const eod = new Date(etaDate.getTime() + 1000 * 60 * DEBRIEFING_MINUTES);
    return eod.toISOString();
};

export const calcElapsedTime = (eod, rpt) => {
    if (!eod || !rpt) {
        return 0;  
    }
    
    if (isISODateString(eod)) {
        eod = parseISO(eod); 
    }

    if (isISODateString(rpt)) {
        rpt = parseISO(rpt); 
    }

    if (eod < rpt) {
        eod.setDate(eod.getDate() + 1);  
    }

    const elapsedHours = (eod - rpt) / (1000 * 60 * 60);
    return elapsedHours;
};

export const calcRouteDays = (eod, rpt) => {
    if (!eod || !rpt) {
        return 0;  
    }
    
    if (eod < rpt) {
        eod = addDays(eod, 1);
    }

    eod = addMinutes(eod, TIMEZONE_OFFSET_MINUTES);
    rpt = addMinutes(rpt, TIMEZONE_OFFSET_MINUTES);

    const eodZonedTime = toZonedTime(eod, 'UTC');
    const rptZonedTime = toZonedTime(rpt, 'UTC');
  
    let routeDays = differenceInCalendarDays(eodZonedTime, rptZonedTime) + 1;
    
    const rptHour = getHours(rptZonedTime);
    if (rptHour >= 0 && rptHour <= 3) {
        routeDays += 1;
    }

    const eodHour = getHours(eodZonedTime);
    if (eodHour >= 0 && eodHour <= 3 && routeDays > 1) {
        routeDays -= 1;
    }
    
    return routeDays;
};

export const mapFlightsFromCrewRoutes = (crewRoutes) => {
    const flightsMap = new Map();

    const flightsWithCrewMembers = crewRoutes.reduce((acc, cur) => {
        const legs = cur.routeDetails;
        const crewMembers = cur.crewMembers;

        legs.forEach(leg => {
            const key = `${leg.flightDate}|${leg.flightDesc}|${leg.dep}`;
            const sector = `${leg.dep}-${leg.arr}`;

            if (leg.pax) {
                if (flightsMap.has(key)) {
                    const existing = flightsMap.get(key);
                    existing.crewMembers = [
                        ...new Set([...existing.crewMembers, ...crewMembers])
                    ];
                } else {
                    const flightWithKey = {
                        ...leg,
                        key,
                        sector,
                        crewMembers,  
                    };

                    flightsMap.set(key, flightWithKey);
                    acc.push(flightWithKey); 
                }
            }
        });

        return acc;
    }, []);

    const sortedFlightsWithCrewMembers = sortMultipleFields(Object.values(flightsWithCrewMembers), [
        { field: 'flightDate', direction: 'asc' },
        { field: 'etd', direction: 'asc' },
    ]);

    return sortedFlightsWithCrewMembers;
}

export const filterFlights = ({ flights, filters, timeMode }) => {
    if (!Array.isArray(flights)) return [];
    if (!filters) return flights;

    return flights.filter((flight) => {
        const etdStr = timeMode === 'UTC' 
            ? flight.etd?.substring(0, 5) 
            : flight.etdLocalBase?.substring(0, 5);
        const minEtd = filters.minEtd;
        const maxEtd = filters.maxEtd;

        if (minEtd && (!etdStr || etdStr < minEtd)) return false;
        if (maxEtd && (!etdStr || etdStr > maxEtd)) return false;

        return Object.entries(filters).every(([key, value]) => {
            if (!value) return true;
            if (key === 'minEtd' || key === 'maxEtd') return true;

            if (key === 'flightDate') {
                const field = timeMode === 'UTC' ? key : `${key}LocalBase`;
                const formattedFlightDate = flight[field] ? format(new Date(flight[field]), 'yyyy-MM-dd') : null;
                const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                return formattedFlightDate === formattedFilterDate;
            }

            if (key === 'flightNo') {
                return flight[key] === Number(value);
            }

            if (key === 'sector') {
                return flight[key]?.toLowerCase().includes(value.toLowerCase());
            }

            if (key === 'acType' || key === 'flightReg') {
                return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
            }

            return flight[key] === value;
        });
    });
}

export const filterCrewRoutes = ({ crewRoutes, filters, timeMode }) => {
    if (!Array.isArray(crewRoutes)) {
        return []; 
    }

    if (!filters) return crewRoutes; 

    return crewRoutes.filter((route) =>
        Object.entries(filters).every(([key, value]) => {
            if (!value) return true;
    
            if (key === 'routeDate') {
                const field = timeMode === 'UTC' ? key : `${key}LocalBase`;
                const formattedRouteDate = route[field] ? format(new Date(route[field]), 'yyyy-MM-dd') : null;
                const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                return formattedRouteDate === formattedFilterDate;
            }
            
            if (key === 'crewRoute'  || key === 'routeNo' || key === 'flightNo') {
                return route[key]?.toLowerCase().includes(value.toLowerCase());
            }

            if (key === 'acType' || key === 'flightReg') {
                return Array.isArray(value) ? value.includes(route[key]) : route[key] === value;
            }

            return route[key] === value;
        })
    );
};

export const filterCrewMember = (member, filters) => {
    return Object.entries(filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;

        const val = member[key];

        if (['crewName', 'quals'].includes(key)) {
            return String(val || '').toLowerCase().includes(String(value).toLowerCase());
        }

        if (['base', 'post'].includes(key)) {
            return Array.isArray(value) ? value.includes(val) : val === value;
        }

        if (key === 'trainingQuals') {
            return Array.isArray(value)
                ? value.some(v => (val || []).includes(v))
                : (val || []).includes(value);
        }

        if (key === 'isActive') {
          if (value === 'ALL') return true; 
          if (value === 'ACTIVE') return val === true; 
          if (value === 'INACTIVE') return val === false; 
        }

        return val === value;
    });
};

/**
 * Loại bỏ các field chỉ định khỏi dữ liệu.
 * @param {Array|Object} data - Dữ liệu đầu vào (mảng object hoặc 1 object).
 * @param {Array<string>} fields - Danh sách key cần bỏ.
 * @returns {Array|Object} - Dữ liệu mới sau khi loại bỏ field.
 */
export const omitKeys = (data, fields = []) => {
    if (!data) return data;

    // Nếu là mảng object
    if (Array.isArray(data)) {
        return data.map(item => {
            const newItem = { ...item };
            fields.forEach(field => delete newItem[field]);
            return newItem;
        });
    }

    // Nếu chỉ là 1 object
    const newItem = { ...data };
    fields.forEach(field => delete newItem[field]);
    return newItem;
};


export const omitFields = (obj, keys) => {
    return Object.fromEntries(
        Object.entries(obj).filter(([k]) => !keys.includes(k))
    );
}

/**
 * Loại bỏ các field kỹ thuật như _id, __v, createdAt, updatedAt, deletedAt, createdBy.
 * Hoạt động đệ quy cho cả object và array.
 */
export function sanitizeData(data, omitFields = []) {
    if (Array.isArray(data)) {
        // Nếu là mảng, duyệt từng phần tử
        return data.map(item => sanitizeData(item, omitFields));
    } else if (data && typeof data === 'object') {
        // Nếu là object, loại bỏ field không mong muốn
        const cleaned = {};
        for (const [key, value] of Object.entries(data)) {
            if (!omitFields.includes(key)) {
                cleaned[key] = sanitizeData(value, omitFields);
            }
        }
        return cleaned;
    }
    // Nếu là kiểu primitive (string, number, boolean...), giữ nguyên
    return data;
}

